import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useCursor } from "../cursor/useCursor";
import AboutCard from "../folio/AboutCard";
import { CURSORS } from "../cursor/cursors";
import sign_animation from "../../../assets/svgs/sign_animation.svg";
import about from "../../../assets/images/about_img.webp";

export default function AboutContainer() {
  const { cursor } = useCursor();

  const signRef = useRef(null);
  const signInView = useInView(signRef, {
    once: true,
    amount: 0.6,
  });

  return (
    <section
      {...cursor({
        ...CURSORS.default,
        size: 50,
      })}
      className="bg-white h-screen p-2 sm:p-4 md:p-6 lg:p-8"
    >
      <div
        className="
          relative
          h-full
          min-h-[600px]
          w-full
          overflow-hidden
          rounded-[45px]
          bg-[#CFEF00]

          sm:rounded-[65px]
          md:rounded-[80px]
          lg:rounded-[110px]
          xl:rounded-[135px]
        "
      >
        {/* ================================
            BACKGROUND GLOW
        ================================= */}

        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            h-[350px]
            w-[350px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-white/10
            blur-[100px]

            md:h-[600px]
            md:w-[600px]

            lg:h-[800px]
            lg:w-[800px]
            lg:blur-[180px]
          "
        />

        {/* ================================
            IMAGE
        ================================= */}

        <motion.div
          initial={{
            opacity: 0,
            y: 80,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 1,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="
            pointer-events-none
            absolute
            bottom-0
            left-0
            z-0

            h-[42vh]
            w-full

            sm:h-[38%]

            md:h-[55%]

            lg:h-[82%]
            lg:w-1/2
          "
        >
          <img
            src={about}
            alt=""
            className="
              absolute
              bottom-0
              left-0

              h-full
              w-auto
              max-w-none

              object-contain
              object-bottom
              select-none
            "
          />
        </motion.div>

        {/* ================================
            CONTENT
        ================================= */}

        <div
          className="
            relative
            z-10

            flex
            h-full
            w-full
            flex-col

            lg:flex-row
          "
        >
          {/* ================= LEFT SIDE ================= */}

          <div
            className="
              hidden

              lg:flex
              lg:w-1/2
            "
          />

          {/* ================= RIGHT CONTENT ================= */}

          <div
            className="
              flex
              w-full
              flex-1
              items-start
              justify-center

              px-6
              pt-10

              sm:px-10
              sm:pt-14

              md:px-14
              md:pt-16

              lg:w-1/2
              lg:items-center
              lg:justify-start
              lg:px-0
              lg:pr-16
              lg:pt-0

              xl:pr-24
            "
          >
            <div
              className="
                w-full
                max-w-xl
              "
            >
              {/* ===========================
                  EYEBROW
              ============================ */}

              <motion.p
                initial={{
                  opacity: 0,
                  x: 80,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.8,
                }}
                className="
                  font-thesignature
                  text-[clamp(45px,10vw,70px)]
                  leading-none
                  text-[#202020]/70
                "
              >
                Hey
              </motion.p>

              {/* ===========================
                  BIO TEXT
              ============================ */}

              <motion.div
                initial={{
                  opacity: 0,
                  x: 80,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.8,
                  delay: 0.15,
                }}
                className="
                  mt-3
                  font-['Space_Mono']

                  text-[clamp(10px,2.8vw,16px)]
                  leading-[1.65]

                  tracking-normal
                  text-[#202020]
                "
              >
                <p>
                  I'm Badusha, a{" "}
                  <span className="font-bold">
                    B.Tech graduate in Computer Science
                  </span>{" "}
                  from{" "}
                  <span className="font-bold">
                    Government Engineering College, Thrissur
                  </span>.
                </p>

                <p className="mt-3 sm:mt-4">
                  I love turning ideas into working software, from{" "}
                  <span className="font-bold">
                    full-stack web apps
                  </span>{" "}
                  and{" "}
                  <span className="font-bold">
                    Flutter
                  </span>{" "}
                  mobile builds, to{" "}
                  <span className="font-bold">
                    machine learning
                  </span>{" "}
                  pipelines and{" "}
                  <span className="font-bold">
                    cybersecurity
                  </span>{" "}
                  tools.
                </p>

                <p className="mt-3 sm:mt-4">
                  Outside of code, I lead creative teams at{" "}
                  <span className="font-bold">
                    muLearn
                  </span>{" "}
                  and{" "}
                  <span className="font-bold">
                    IEDC
                  </span>
                  , and I'm usually behind a camera —{" "}
                  <span className="font-bold">
                    photography
                  </span>{" "}
                  is my go-to way to unwind.
                </p>
              </motion.div>

              {/* ===========================
                  STATS
              ============================ */}

              <motion.div
                initial={{
                  opacity: 0,
                  x: 80,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.8,
                  delay: 0.3,
                }}
                className="
                  mt-4
                  flex
                  flex-wrap
                  items-center

                  gap-x-4
                  gap-y-2

                  sm:mt-6
                  sm:gap-x-6

                  font-['Space_Mono']
                  text-[#202020]
                "
              >
                <div className="flex items-baseline gap-1 sm:gap-2">
                  <span className="text-[clamp(17px,5vw,35px)] font-bold">
                    6+
                  </span>

                  <span className="text-[8px] uppercase tracking-wider text-[#202020]/70 sm:text-[11px]">
                    Projects
                  </span>
                </div>

                <span className="h-4 w-px bg-[#202020]/30" />

                <div className="flex items-baseline gap-1 sm:gap-2">
                  <span className="text-[clamp(17px,5vw,35px)] font-bold">
                    4+
                  </span>

                  <span className="text-[8px] uppercase tracking-wider text-[#202020]/70 sm:text-[11px]">
                    Years Coding
                  </span>
                </div>

                <span className="h-4 w-px bg-[#202020]/30" />

                <div className="flex items-baseline gap-1 sm:gap-2">
                  <span className="text-[clamp(17px,5vw,35px)] font-bold">
                    2026
                  </span>

                  <span className="text-[8px] uppercase tracking-wider text-[#202020]/70 sm:text-[11px]">
                    CS Grad
                  </span>
                </div>
              </motion.div>

              {/* ===========================
                  SIGNATURE
              ============================ */}

              <div
                ref={signRef}
                className="
                  flex
                  h-12
                  w-full
                  justify-end
                  opacity-80

                  sm:h-16
                  md:h-24
                "
              >
                {signInView && (
                  <img
                    className="
                      w-28
                      object-contain

                      sm:w-36
                      md:w-40
                    "
                    src={sign_animation}
                    alt=""
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ================================
            ABOUT CARD
        ================================= */}

        <AboutCard />
      </div>
    </section>
  );
}